
import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../src/config";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SuccessModal from "./Modal/SuccessModal";
import { parseLocalDate } from "../utils/dateUtils";

const BookingReview = () => {
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({}); // For inline validation
  const navigate = useNavigate();
  const [showGuestConfirm, setShowGuestConfirm] = useState(false);
  const [sendCredentials, setSendCredentials] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discount_amount }
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);


  useEffect(() => {
    console.log("Booking Summary:", localStorage.getItem("draftBooking"));
    const bookingDraft = JSON.parse(localStorage.getItem("draftBooking") || "{}");
    if (bookingDraft && Object.keys(bookingDraft).length > 0) {
      if (!bookingDraft.guest) bookingDraft.guest = {};
      if (!bookingDraft.guest.children_ages) bookingDraft.guest.children_ages = [];

      const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (loggedInUser && (loggedInUser.firstName || loggedInUser.lastName)) {
        bookingDraft.guest[`first_name_0`] = loggedInUser.firstName || "";
        bookingDraft.guest[`last_name_0`] = loggedInUser.lastName || "";
        bookingDraft.guest.email = loggedInUser.email || "";
        bookingDraft.guest.phone = loggedInUser.phone || "";
        bookingDraft.guest.address = loggedInUser.address || "";
        bookingDraft.guest[`title_0`] = loggedInUser.gender === "female" ? "Ms" : "Mr";
      }

      setBookingData(bookingDraft);
    }

    const searchParams = JSON.parse(localStorage.getItem("searchParams") || "{}");
    console.log("Search Params:", searchParams);
    if (Object.keys(searchParams).length > 0) {

      setBookingData((prev) => ({
        ...prev,
        summary: {
          ...prev.summary,
          destination_id: searchParams.destination_id,
          destination_name: searchParams.destination_name,
          check_in: searchParams.checkin_date,
          check_out: searchParams.checkout_date,
          adults: parseInt(searchParams.adults) || 1,
          children: parseInt(searchParams.kids) || 0,
        },
      }));

    }
  }, []);

  if (!bookingData) {
    return (
      <section className="bookingdlcontainer">
        <div className="container">
          <p>No booking details found. Please start your booking again.</p>
        </div>
      </section>
    );
  }

  const { stay, room, summary, guest } = bookingData;


  const handleInputChange = (field, value) => {
    setBookingData((prev) => {
      const updated = {
        ...prev,
        summary: {
          ...prev.summary,
          [field]: value,
        },
      };

      // Auto adjust guest structure when adults change
      if (field === "adults") {
        const guest = { ...updated.guest };

        for (let i = 0; i < value; i++) {
          if (!guest[`first_name_${i}`]) guest[`first_name_${i}`] = "";
          if (!guest[`last_name_${i}`]) guest[`last_name_${i}`] = "";
          if (!guest[`title_${i}`]) guest[`title_${i}`] = "Mr";
        }

        updated.guest = guest;
      }

      // Auto adjust children ages
      if (field === "children") {
        const guest = { ...updated.guest };
        guest.children_ages = Array(value).fill("");
        updated.guest = guest;
      }

      return updated;
    });
  };

  const validateFields = () => {
    const newErrors = {};   // ✅ ADD THIS LINE

    // Track all full names
    const nameSet = new Set();

    Array.from({ length: summary?.adults || 1 }, (_, i) => {
      const firstName = guest[`first_name_${i}`]?.trim().toLowerCase() || "";
      const lastName = guest[`last_name_${i}`]?.trim().toLowerCase() || "";

      if (firstName.length < 2) {
        newErrors[`first_name_${i}`] = "Please enter a valid first name";
      }

      if (lastName.length < 2) {
        newErrors[`last_name_${i}`] = "Please enter a valid last name";
      }

      if (firstName && lastName && firstName === lastName) {
        newErrors[`last_name_${i}`] = "First and last name cannot be same";
      }

      const fullName = `${firstName} ${lastName}`;
      if (firstName && lastName) {
        if (nameSet.has(fullName)) {
          newErrors[`first_name_${i}`] = "Duplicate guest name not allowed";
          newErrors[`last_name_${i}`] = "Duplicate guest name not allowed";
        } else {
          nameSet.add(fullName);
        }
      }
    });

    // Email validation
    if (!guest?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone validation
    if (!guest?.phone || !/^\d{10}$/.test(guest.phone)) {
      newErrors.phone = "Please enter a valid 10-digit mobile number";
    }

    // Address validation
    if (!guest?.address || guest.address.trim().length < 5) {
      newErrors.address = "Please enter a valid address";
    }

    // Children names validation
    Array.from({ length: summary?.children || 0 }, (_, i) => {
      const firstName = guest[`child_first_name_${i}`]?.trim().toLowerCase() || "";
      const lastName = guest[`child_last_name_${i}`]?.trim().toLowerCase() || "";

      if (!firstName || !lastName) return;

      const fullName = `${firstName} ${lastName}`;

      if (nameSet.has(fullName)) {
        newErrors[`child_first_name_${i}`] = "Duplicate guest name not allowed";
        newErrors[`child_last_name_${i}`] = "Duplicate guest name not allowed";
      } else {
        nameSet.add(fullName);
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Delay scrolling to ensure input is rendered
      setTimeout(() => {
        const firstErrorField = Object.keys(newErrors)[0];
        console.log("Scrolling to field with error:", firstErrorField);
        const el = document.querySelector(`[name="${firstErrorField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.focus();
        }
      }, 100); // 100ms delay
    }


    return Object.keys(newErrors).length === 0;
  };

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    setPromoError("");
    try {
      const res = await axios.post(`${API_BASE_URL}bookings/validate-promo`, {
        code: promoInput.trim(),
        order_value: Number(summary?.total_room_price || 0),
      });
      if (res.data.success) {
        setAppliedPromo(res.data.data);
      } else {
        setPromoError(res.data.message || "Invalid promo code");
      }
    } catch (err) {
      setPromoError(err?.response?.data?.message || "Invalid promo code");
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput("");
    setPromoError("");
  };

  const submitBooking = async (sendTempCredentials = false) => {
    try {
      setLoading(true);

      const token = localStorage.getItem("accessToken") || "";

      // guestsArr logic stays SAME
      const guestsArr = [];

      Array.from({ length: summary.adults || 1 }, (_, i) => {
        guestsArr.push({
          title: guest[`title_${i}`] || "Mr",
          first_name: guest[`first_name_${i}`] || "",
          last_name: guest[`last_name_${i}`] || "",
          children_age: "",
        });
      });

      (guest.children_ages || []).forEach((age, i) => {
        if (parseInt(age) <= 12) {
          guestsArr.push({
            title: guest[`child_title_${i}`] || "Mr",
            first_name: guest[`child_first_name_${i}`] || "",
            last_name: guest[`child_last_name_${i}`] || "",
            children_age: age.toString(),
          });
        }
      });

      const payload = {
        stay_id: stay.id,

        //. IMPORTANT CHANGE
        selected_rooms: bookingData?.selected_rooms || [],

        check_in: summary.check_in,
        check_out: summary.check_out,

        adults: parseInt(summary.adults) || 1,
        children: guestsArr.filter(g => g.children_age).length,

        guests: guestsArr,
        guest_count: guestsArr.length,

        //. PRIMARY GUEST (REQUIRED BY BACKEND)
        first_name: guestsArr[0]?.first_name || "",
        last_name: guestsArr[0]?.last_name || "",

        email: guest.email,
        phone: guest.phone,
        address: guest.address || "",

        send_temp_credentials: sendTempCredentials,
        promo_code: appliedPromo?.code || undefined
      };

      const url = token
        ? `${API_BASE_URL}bookings/confirm-combination`
        : `${API_BASE_URL}bookings/guest/confirm-combination`;

      const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};

      const response = await axios.post(url, payload, config);

      if (response.data.success) {
        const { payment_url, booking_id, booking_code } = response.data.data;

        localStorage.setItem(
          "currentBookingId",
          JSON.stringify({
            booking_id,
            booking_code   // correct value
          })
        );

        if (typeof window.fbq === "function") {
          window.fbq("track", "InitiateCheckout", {
            value: Number(summary?.total_room_price || 0),
            currency: "INR",
            content_ids: [booking_code],
            num_items: (payload.selected_rooms || []).length,
          });
        }

        // REDIRECT TO RAZORPAY PAGE
        window.location.href = payment_url;
      } else {
        toast.error(response.data.message || "Booking failed");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Something went wrong"
      );
    } finally {
      setLoading(false);
      setShowGuestConfirm(false);
    }
  };


  const handleConfirmBooking = async () => {
    if (!validateFields()) return;

    const token = localStorage.getItem("accessToken");

    // If NOT logged in, show confirmation popup first
    if (!token) {
      setShowGuestConfirm(true);
      return;
    }

    // Logged-in user → directly proceed
    submitBooking(true);
  };


  return (
    <section className="bookingdlcontainer">
      <div className="container">
        {errors.form && <div className="alert alert-danger">{errors.form}</div>}
        <div className="row">
          <div className="col-12">
            <div className="bookingdlouter">
              <div className="row">
                {/* Left Section */}
                <div className="col-xxl-8 col-xl-8 col-lg-8 col-md-7 col-sm-12 col-12 pe-lg-5">
                  {/* Existing content remains unchanged */}
                  <div className="filterouter align-items-center">
                    <div className="breadcrumbinner">
                      <div className="w-100">
                        <ol className="breadcrumb">
                          <li><Link to="/">Home</Link></li>
                          <li>
                            <Link
                              to="/stays"
                              state={{
                                destination_name:
                                  stay?.Location?.location_name || stay?.location_name,
                                destination_id:
                                  stay?.Location?.id || stay?.location_id || "",
                                adults: "1",
                                kids: ""
                              }}
                            >
                              Homestays in {stay?.Location?.location_name || stay?.location_name}
                            </Link>
                          </li>

                          <li><Link to={`/property/${stay?.id}`}>{stay?.title}</Link></li>
                          <li className="active">Review your Booking</li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  {/* Property Heading */}
                  <div className="bookingdltheadingouter">
                    <div className="dltheading">
                      <span className="startp">
                        <i className="fa fa-star"></i>
                        <span>{stay?.homestay_average_rating || stay?.host_details?.rating || 0} stars</span>
                      </span>
                      <h2>{stay?.title}, {stay?.Location?.location_name || stay?.location_name}</h2>
                      <p>{stay?.description}</p>
                    </div>
                  </div>

                  {/* Property Image */}
                  <div className="row gx-0 mb-3 mb-md-5">
                    <div className="col-12">
                      <div className="bookingimg">
                        <img
                          src={stay?.cover_image || stay?.galleries?.[0]?.image_url || "images/booking-img.jpg"}
                          alt={stay?.title}
                        />
                      </div>
                    </div>
                  </div>
                  <h3>Selected Rooms</h3>

                  {bookingData?.selected_rooms?.length > 0 ? (
                    <div style={{ marginTop: "12px" }}>
                      {bookingData.selected_rooms.map((sel, index) => {
                        const roomDetails = stay?.rooms?.find(r => r.id === sel.room_id);

                        return (
                          <div
                            key={index}
                            style={{
                              borderRadius: "10px",
                              padding: "12px 14px",
                              marginBottom: "20px",
                              background: "#ffffff",
                              border: "1px solid #f1f1f1"
                            }}
                          >
                            {/* Top Row */}
                            <div style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "6px"
                            }}>
                              <span style={{
                                fontWeight: "600",
                                fontSize: "15px",
                                color: "#111"
                              }}>
                                {roomDetails?.title || "Room"}
                              </span>

                              <span style={{
                                fontWeight: "600",
                                fontSize: "15px",
                                color: "#373758"
                              }}>
                                ₹{roomDetails?.price_min}
                              </span>
                            </div>

                            {/* Bottom Row */}
                            <div style={{
                              fontSize: "14px",
                              color: "#373758",
                              fontWeight: "500"
                            }}>
                              {sel.room_count} room{sel.room_count > 1 ? "s" : ""}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ fontSize: "13px", color: "#888", marginTop: "8px" }}>
                      No rooms selected
                    </p>
                  )}

                  {/* Trip Details */}
                  <h3>Your Trip</h3>
                  <div className="row gx-3 gx-lg-4">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Check-in</label>
                        <DatePicker
                          selected={parseLocalDate(summary?.check_in)}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          wrapperClassName="w-100"
                          disabled
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Check-out</label>
                        <DatePicker
                          selected={parseLocalDate(summary?.check_out)}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="dd/mm/yyyy"
                          className="form-control"
                          wrapperClassName="w-100"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row gx-3 gx-lg-4 mt-2">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Adults</label>
                        <input
                          type="number"
                          className="form-control"
                          value={summary?.adults || 1}
                          onChange={(e) =>
                            handleInputChange("adults", parseInt(e.target.value) || 1)
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Children</label>
                        <input
                          type="number"
                          className="form-control"
                          value={summary?.children}
                          onChange={(e) => handleInputChange("children", parseInt(e.target.value))}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Rooms</label>
                        <input
                          type="number"
                          className="form-control"
                          value={summary?.room_count}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  {/* Guest Details */}
                  <h3>Guest Details</h3>
                  <div className="row gx-3 gx-lg-4">
                    {/* Adults */}
                    {Array.from({ length: summary?.adults || 1 }, (_, index) => (
                      <React.Fragment key={`adult-${index}`}>
                        <div className="col-md-3">
                          <div className="form-group">
                            <select
                              className="form-select"
                              value={guest[`title_${index}`] || "Mr"}
                              onChange={(e) =>
                                setBookingData(prev => ({
                                  ...prev,
                                  guest: { ...prev.guest, [`title_${index}`]: e.target.value }
                                }))
                              }
                            >
                              <option>Mr</option>
                              <option>Mrs</option>
                              <option>Miss</option>
                            </select>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <input
                              type="text"
                              name={`first_name_${index}`}
                              className="form-control"
                              placeholder="First Name"
                              value={guest[`first_name_${index}`] || ""}
                              onChange={(e) =>
                                setBookingData(prev => ({
                                  ...prev,
                                  guest: { ...prev.guest, [`first_name_${index}`]: e.target.value }
                                }))
                              }
                            />
                            {errors[`first_name_${index}`] && <small className="text-danger">{errors[`first_name_${index}`]}</small>}
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <input
                              type="text"
                              name={`last_name_${index}`}
                              className="form-control"
                              placeholder="Last Name"
                              value={guest[`last_name_${index}`] || ""}
                              onChange={(e) =>
                                setBookingData(prev => ({
                                  ...prev,
                                  guest: { ...prev.guest, [`last_name_${index}`]: e.target.value }
                                }))
                              }
                            />
                            {errors[`last_name_${index}`] && <small className="text-danger">{errors[`last_name_${index}`]}</small>}
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Adult"
                              value="Adult"
                              readOnly
                              style={{ textAlign: "center", fontWeight: "bold" }}
                            />
                          </div>
                        </div>
                      </React.Fragment>
                    ))}

                    {/* Children */}
                    {Array.from({ length: summary?.children || 0 }, (_, index) => {
                      const age = guest?.children_ages?.[index] || "";
                      if (age > 12) return null;
                      return (
                        <React.Fragment key={`child-${index}`}>
                          <div className="col-md-3">
                            <div className="form-group">
                              <select
                                className="form-select"
                                value={guest[`child_title_${index}`] || "Mr"}
                                onChange={(e) =>
                                  setBookingData(prev => ({
                                    ...prev,
                                    guest: { ...prev.guest, [`child_title_${index}`]: e.target.value }
                                  }))
                                }
                              >
                                <option>Mr</option>
                                <option>Mrs</option>
                                <option>Miss</option>
                              </select>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <input
                              type="text"
                              name={`child_first_name_${index}`}
                              className="form-control"
                              placeholder="First Name"
                              value={guest[`child_first_name_${index}`] || ""}
                              onChange={(e) =>
                                setBookingData(prev => ({
                                  ...prev,
                                  guest: { ...prev.guest, [`child_first_name_${index}`]: e.target.value }
                                }))
                              }
                            />
                            {errors[`child_first_name_${index}`] && <small className="text-danger">{errors[`child_first_name_${index}`]}</small>}

                          </div>
                          <div className="col-md-3">
                            <input
                              type="text"
                              name={`child_last_name_${index}`}
                              className="form-control"
                              placeholder="Last Name"
                              value={guest[`child_last_name_${index}`] || ""}
                              onChange={(e) =>
                                setBookingData(prev => ({
                                  ...prev,
                                  guest: { ...prev.guest, [`child_last_name_${index}`]: e.target.value }
                                }))
                              }
                            />
                            {errors[`child_last_name_${index}`] && <small className="text-danger">{errors[`child_last_name_${index}`]}</small>}

                          </div>

                          <div className="col-md-3">
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Age"
                              min={0}
                              max={12}
                              value={age}
                              onKeyDown={(e) => {
                                // Prevent invalid characters in number input
                                if (["e", "E", "+", "-"].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onChange={(e) => {
                                let val = e.target.value;

                                // Prevent typing values greater than 12
                                if (val === "") val = "";
                                else if (parseInt(val) > 12) val = 12;
                                else val = parseInt(val);

                                setBookingData((prev) => {
                                  const ages = prev.guest.children_ages ? [...prev.guest.children_ages] : [];
                                  ages[index] = val;
                                  return { ...prev, guest: { ...prev.guest, children_ages: ages } };
                                });
                              }}
                              style={{
                                backgroundColor: "#ffe6e6",
                                textAlign: "center",
                                fontWeight: "bold",
                              }}
                            />
                            <small className="text-muted d-block mt-1">
                              *Below 12 years, please enter age*
                            </small>
                          </div>

                        </React.Fragment>
                      );
                    })}

                    {/* Contact Info */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group">
                        <label>Email Address</label>
                        <input
                          type="text"
                          name="email"
                          className="form-control"
                          value={guest?.email || ""}
                          onChange={(e) =>
                            setBookingData((prev) => ({
                              ...prev,
                              guest: { ...prev.guest, email: e.target.value },
                            }))
                          }
                        />
                        {errors.email && <small className="text-danger">{errors.email}</small>}
                      </div>
                    </div>

                    <div className="col-md-6 mt-3">
                      <div className="form-group">
                        <label>Mobile Number</label>
                        <input
                          type="text"
                          name="phone"
                          className="form-control"
                          value={guest?.phone || ""}
                          maxLength={10}
                          onKeyDown={(e) => {
                            // Block invalid keys
                            if (
                              ["e", "E", "+", "-", ".", ","].includes(e.key) ||
                              (e.key.length === 1 && !/[0-9]/.test(e.key))
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) =>
                            setBookingData((prev) => ({
                              ...prev,
                              guest: { ...prev.guest, phone: e.target.value },
                            }))
                          }
                        />
                        {errors.phone && <small className="text-danger">{errors.phone}</small>}
                      </div>
                    </div>

                    <div className="col-md-12 mt-3">
                      <div className="form-group">
                        <label>Address</label>
                        <input
                          type="text"
                          name="address"
                          className="form-control"
                          value={guest?.address || ""}
                          onChange={(e) =>
                            setBookingData((prev) => ({
                              ...prev,
                              guest: { ...prev.guest, address: e.target.value },
                            }))
                          }
                        />
                        {errors.address && <small className="text-danger">{errors.address}</small>}
                      </div>
                    </div>
                  </div>


                  <h3 className="mb-3">Ground Rules</h3>

                  <div className="ground-rules-box">
                    <div className="ground-row">
                      <span className="label">Check In:  </span>
                      <span className="value">{stay?.checkin || "—"}</span>
                    </div>

                    <div className="ground-row">
                      <span className="label">Check out:  </span>
                      <span className="value">{stay?.checkout || "—"}</span>
                    </div>

                    {stay?.cancellation_policy && (
                      <div className="cancellation-box">
                        <p className="title">Cancellation Policy -</p>
                        <ul>
                          {stay.cancellation_policy.split(",").map((item, index) => (
                            <li key={index}>{item.trim()}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>


                <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-5 col-sm-12 col-12">
                  <div className="pricertbx">
                    <h3>Price Breakup</h3>

                    {/* Per Room Breakdown */}
                    {(() => {
                      const nights = summary?.nights || 1;
                      const roomCount = summary?.room_count || 1;

                      const pricePerNight =
                        summary?.room?.price_per_night ||
                        summary?.price_per_night ||
                        summary?.room_price ||
                        0;

                      return (
                        <div className="d-flex gx-0 justify-content-between priceborder pb-2 pb-md-4 pt-2 pt-md-4">
                          <span>
                            <p>
                              <strong>Room {roomCount}:</strong>{" "}
                              <span className="bestprice">
                                ₹{(pricePerNight / nights).toFixed(2)} per night × {nights} night{nights > 1 ? "s" : ""}
                              </span>
                            </p>
                          </span>

                          <span className="p-0 prtvalue text-end">
                            ₹{Number(summary?.total_room_price_without_tax || 0).toFixed(2)}
                          </span>
                        </div>
                      );
                    })()}

                    {/* Taxes & Fees — bucketed for the guest-facing review screen.
                        GST, platform service fee, and GST on the platform fee
                        still stay separately itemized on the invoice/booking
                        confirmation email; this page just shows the total of
                        everything besides the room charge, computed as
                        Total - Room Charge so it can never drift out of sync
                        with whatever fee fields the backend adds later. */}
                    <div className="d-flex gx-0 justify-content-between priceborder pb-2 pt-2">
                      <p className="mb-0">Taxes &amp; Fees</p>
                      <span className="p-0 prtvalue text-end">
                        ₹{(
                          Number(summary?.total_room_price || 0) -
                          Number(summary?.total_room_price_without_tax || 0)
                        ).toFixed(2)}
                      </span>
                    </div>

                    {/* Promo code */}
                    <div className="priceborder pb-2 pt-2">
                      {!appliedPromo ? (
                        <>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Have a promo code?"
                              value={promoInput}
                              onChange={(e) => {
                                setPromoInput(e.target.value.toUpperCase());
                                setPromoError("");
                              }}
                              style={{ textTransform: "uppercase" }}
                            />
                            <button
                              type="button"
                              className="theme-btn"
                              style={{ whiteSpace: "nowrap" }}
                              onClick={handleApplyPromo}
                              disabled={promoLoading || !promoInput.trim()}
                            >
                              {promoLoading ? "..." : "Apply"}
                            </button>
                          </div>
                          {promoError && (
                            <small className="text-danger d-block mt-1">{promoError}</small>
                          )}
                        </>
                      ) : (
                        <div
                          className="d-flex justify-content-between align-items-center"
                          style={{ background: "#eaf6ee", borderRadius: "6px", padding: "8px 12px" }}
                        >
                          <span style={{ fontSize: "13px", color: "#1a5c33" }}>
                            🎉 {appliedPromo.code} applied — ₹{Number(appliedPromo.discount_amount).toFixed(2)} off
                          </span>
                          <span
                            role="button"
                            onClick={handleRemovePromo}
                            style={{ fontSize: "12px", color: "#888", cursor: "pointer", fontWeight: 600 }}
                          >
                            Remove
                          </span>
                        </div>
                      )}
                    </div>

                    {appliedPromo && (
                      <div className="d-flex gx-0 justify-content-between priceborder pb-2 pt-2">
                        <p className="mb-0">Promo discount ({appliedPromo.code})</p>
                        <span className="p-0 prtvalue text-end" style={{ color: "#1a7a4c" }}>
                          −₹{Number(appliedPromo.discount_amount).toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Total (Inclusive of all charges) */}
                    <div className="priceborder pb-2 pt-2 d-flex justify-content-between total">
                      <p className="mb-0">
                        <strong>Total</strong>
                      </p>
                      <span className="p-0 prtvalue text-end">
                        <strong>
                          ₹{(
                            Number(summary?.total_room_price || 0) -
                            Number(appliedPromo?.discount_amount || 0)
                          ).toFixed(2)}
                        </strong>
                      </span>
                    </div>

                    {/* Confirm Booking Button */}
                    <div className="mt-3">
                      <button
                        className="theme-btn w-100"
                        onClick={handleConfirmBooking}
                        disabled={loading}
                      >
                        {loading ? "Confirming..." : "Confirm Booking"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SuccessModal
        show={showGuestConfirm}
        title="Create an account?"
        message="We can create a temporary account for you and send login details to your email. This is completely optional."
        confirmText="Yes, send login details"
        cancelText="No, continue as guest"
        onConfirm={() => {
          setShowGuestConfirm(false);
          submitBooking(true);
        }}
        onCancel={() => {
          setShowGuestConfirm(false);
          submitBooking(false);
        }}
      />


    </section>
  );
};
export default BookingReview;